import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useLogout } from '../../application/hooks/useAuth';
import { useDashboardStats } from '../../application/hooks/useDashboard';
import { useNotices } from '../../application/hooks/useNotices';
import { useTranslation } from '../../application/hooks/useTranslation';
import { DelinquencyStatus } from '../../domain/enums/SystemEnums';
import { TierLevel, TIER_DESIGNATION_MAP } from '../../domain/enums/TierLevel';
import { RequireAuth } from '../routes/guards';
import { TopBar } from '../components/layout/TopBar';
import { MobileBottomNav } from '../components/layout/BottomNav';
import { Sidebar } from '../components/layout/Sidebar';
import { DelinquencyBanner } from '../components/layout/DelinquencyBanner';
import { AccountClosedScreen } from '../components/delinquency/AccountClosedScreen';
import { DevRoleSwitcher } from '../components/dev/DevRoleSwitcher';
import { AppLayout } from './AppLayout';
import './Shell.css';

/**
 * W02 app shell behind RequireAuth: top bar + role-adaptive nav (mobile bottom nav,
 * desktop sidebar) + persistent delinquency banner (W14) + routed content.
 */
export default function Shell() {
  return (
    <RequireAuth>
      <ShellChrome />
    </RequireAuth>
  );
}

function ShellChrome() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const logout = useLogout();
  const stats = useDashboardStats();
  const notices = useNotices();
  const [menuOpen, setMenuOpen] = useState(false);

  const tier = user?.tier ?? TierLevel.L1;
  const delinquencyStatus = stats.data?.delinquencyStatus ?? DelinquencyStatus.ACTIVE;
  const delinquencyLocked =
    delinquencyStatus === DelinquencyStatus.DISABLED_DEFAULT ||
    delinquencyStatus === DelinquencyStatus.INACTIVE ||
    delinquencyStatus === DelinquencyStatus.CLOSED;

  const handleLogout = () => {
    logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) });
  };

  return (
    <AppLayout.Root>
      <TopBar
        userName={user?.fullName ?? '…'}
        tierLabel={user?.tier ? (TIER_DESIGNATION_MAP[user.tier] ?? user.tier) : ''}
        unreadNotices={notices.data?.length ?? 0}
        notices={notices.data}
        isLoadingNotices={notices.isPending}
        onLogout={handleLogout}
        onToggleMenu={() => setMenuOpen((v) => !v)}
      />
      <DelinquencyBanner status={delinquencyStatus} nextDueDate={stats.data?.nextDueDate ?? null} />
      <div className={`menu-drawer${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen} data-testid="menu-drawer">
        <Sidebar tier={tier} />
      </div>
      {menuOpen && (
        <button
          className="menu-scrim"
          aria-label={t('common.close')}
          data-testid="menu-scrim"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <div className="shell-body">
        <Sidebar tier={tier} />
        <AppLayout.Content>
          {delinquencyStatus === DelinquencyStatus.CLOSED ? (
            <AccountClosedScreen fullName={user?.fullName} userId={user?.apartmentId} />
          ) : (
            <Outlet />
          )}
        </AppLayout.Content>
      </div>
      <MobileBottomNav tier={tier} delinquencyLocked={delinquencyLocked} />
      {import.meta.env.VITE_ENABLE_MOCKS === 'true' && <DevRoleSwitcher />}
      <span className="visually-hidden">{locale === 'bn' ? 'বাংলা' : 'English'}</span>
    </AppLayout.Root>
  );
}
